import { Layer, ObjectCategory, ZIndexes } from "@common/constants";
import type { GunDefinition } from "@common/definitions/items/guns";
import { TintedParticles } from "@common/definitions/obstacles";
import { getLayerContainer } from "@common/utils/layer";
import { Vec, type Vector } from "@common/utils/vector";
import { CanvasSource, Container, Rectangle, Sprite, Texture } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import type { Building } from "../objects/building";
import type { Player } from "../objects/player";
import { PIXI_SCALE } from "../utils/constants";
import { softwareRendering } from "../utils/graphicsAcceleration";
import { TrenchioSprite } from "../utils/pixi";
import { WarLook } from "../war/warLook";
import { AtticDark } from "./atticDark";
import { Lighting, multiplyTint } from "./lighting";
import { CameraManager } from "./cameraManager";
import { CombatSounds } from "./combatSounds";
import { casingSound, type GunTraits, gunTraits, type ImpactKind, SoundThrottle, TakePicker } from "./combatSoundsLogic";
import { MapManager } from "./mapManager";
import { SoundManager } from "./soundManager";

/**
 * What a shot leaves in the air and on the ground (Settings → Graphics → "Muzzle smoke, sparks and casings",
 * cv_combat_fx):
 *  - at the muzzle: a glow that lights the ground around it for an instant, smoke puffs blown forward then drifting with
 *    the wind (more and heavier for a sniper rifle or a shotgun, a wisp for a suppressed gun, none for the BB gun),
 *    a few unburnt-powder sparks from shotguns and revolvers, dust kicked up off the ground by a sniper's blast
 *  - where a bullet strikes: sparks off metal (they arc down and skip once), chips and dust off stone, splinters and
 *    sawdust off wood, a spray of sand off earth and sandbags, shards off glass; armour throws sparks too
 *  - casings: they fly out, spin, bounce and lie where they land for a while, ringing as they land (managers/
 *    combatSoundsLogic.ts: casingSound), and sink into water
 *  - a bright head on a rifle's or sniper's tracer (made here, drawn by the bullet: objects/bullet.ts)
 * Everything is drawn with a few shapes painted once (no image files), sprites reused, and capped by the look's quality
 * (high, medium, low: as the blood is).
 *
 * Positions are in the camera's pixels (PIXI_SCALE a game unit), with a height above the ground (z) for what flies:
 * drawn bigger the higher it is, with its shadow falling down and to the right, as the blood's drops are (gore.ts).
 * Decoration only: a fault here is logged once and the effects stay off for the rest of the visit.
 */

const TAU = Math.PI * 2;
const GRAVITY = 1350;
/** the blood's scale (gore.ts): sizes and speeds worked out for players 30 px in radius, made the game's (45 px) */
const M = 1.5;

interface Tier {
    readonly amount: number
    /** a muzzle's flash lights the ground around it (a big glow: costly drawn without a graphics card) */
    readonly light: boolean
    readonly casings: number
    readonly casingLife: number
    readonly bits: number
    readonly puffs: number
    /** the flames' pieces on screen at once (a flash is three to eight: its tongue, root, prongs and light) */
    readonly flames: number
    /** a gun that fired a lot smokes from its barrel a while after (0.86) */
    readonly barrelSmoke: boolean
}

/**
 * 0.98.7 (the owner: "Damage that stays"): the casings and the chips, splinters, shards and grains lie where they
 * fell for the match (`KEEP_MS`), past the caps the oldest going first; the caps are up for it
 */
export const KEEP_MS = 1e9;
const TIERS: Readonly<Record<"high" | "medium" | "low", Tier>> = {
    high: { amount: 1, light: true, casings: 420, casingLife: KEEP_MS, bits: 520, puffs: 110, flames: 140, barrelSmoke: true },
    medium: { amount: 0.7, light: true, casings: 220, casingLife: KEEP_MS, bits: 300, puffs: 64, flames: 90, barrelSmoke: true },
    low: { amount: 0.4, light: false, casings: 90, casingLife: KEEP_MS, bits: 130, puffs: 26, flames: 40, barrelSmoke: false }
};

// ------------------------------------------------------------------------------------------------------- the shapes

interface Shapes {
    readonly glow: Texture
    /** the muzzle's flame: a tongue of burning gas pointing right (its base at the left), soft at its tip */
    readonly flame: Texture
    /** its white-hot root */
    readonly flameCore: Texture
    /** a thin spike of flame (a flash's prongs, pointing right from its base) */
    readonly prong: Texture
    /** the light a flash casts round it: a wide, smooth falloff (the glow is too bright in its middle for it) */
    readonly light: Texture
    readonly smoke: readonly Texture[]
    readonly dust: readonly Texture[]
    readonly spark: Texture
    readonly chips: readonly Texture[]
    readonly splinters: readonly Texture[]
    readonly shards: readonly Texture[]
    readonly grain: Texture
    readonly dot: Texture
    readonly ring: Texture
}

/** a seeded random: the shapes are the same every visit */
const mulberry32 = (seed: number) => (): number => {
    let t = (seed = (seed + 0x6D2B79F5) | 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/** a smoke puff's and a dust cloud's radius at scale 1 */
const SMOKE_R = 40;
const DUST_R = 26;

let shapes: Shapes | undefined;

function buildShapes(): Shapes {
    const W = 512, H = 512;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no 2D canvas for the combat effects' shapes");
    const rand = mulberry32(0xc0b);
    const source = new CanvasSource({ resource: canvas, scaleMode: "linear", autoGenerateMipmaps: true });
    let x = 0, y = 0, rowH = 0;
    const cell = (w: number, h: number): Rectangle => {
        if (x + w > W) {
            x = 0;
            y += rowH + 2;
            rowH = 0;
        }
        const rect = new Rectangle(x, y, w, h);
        x += w + 2;
        rowH = Math.max(rowH, h);
        return rect;
    };
    const texture = (frame: Rectangle): Texture => new Texture({ source, frame });
    const radial = (cx: number, cy: number, r: number, stops: ReadonlyArray<readonly [number, number]>): CanvasGradient => {
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        for (const [at, alpha] of stops) g.addColorStop(at, `rgba(255,255,255,${alpha})`);
        return g;
    };

    // a soft round glow, bright in its middle
    const glowCell = cell(64, 64);
    ctx.fillStyle = radial(glowCell.x + 32, glowCell.y + 32, 32, [[0, 1], [0.18, 0.85], [0.45, 0.32], [0.75, 0.08], [1, 0]]);
    ctx.fillRect(glowCell.x, glowCell.y, 64, 64);

    // the light a flash casts: smooth all the way out (a gaussian's look), so a big one has no visible edge or hot spot
    const lightCell = cell(128, 128);
    ctx.fillStyle = radial(lightCell.x + 64, lightCell.y + 64, 64, [[0, 1], [0.12, 0.86], [0.3, 0.5], [0.5, 0.22], [0.7, 0.07], [0.88, 0.015], [1, 0]]);
    ctx.fillRect(lightCell.x, lightCell.y, 128, 128);

    // the flame: a tongue of gas, widest a third of the way out, burning out to a ragged soft tip. Drawn white (it's
    // tinted and added): bright at its root, thinning along its length, its edges soft
    const flameCell = cell(128, 48);
    {
        const fx = flameCell.x, fy = flameCell.y + 24;
        ctx.save();
        ctx.beginPath();
        ctx.rect(flameCell.x, flameCell.y, 128, 48);
        ctx.clip();
        // a few overlapping lobes along its axis, each softer and smaller further out (a flame isn't a clean cone)
        const lobes: ReadonlyArray<readonly [number, number, number, number]> = [
            [10, 0, 14, 1], [24, 1, 18, 0.95], [40, -1.5, 19, 0.8], [58, 1.5, 17, 0.62], [76, -1, 14, 0.45], [94, 1, 11, 0.3], [110, 0, 8, 0.16]
        ];
        for (const [along, off, r, alpha] of lobes) {
            const g = ctx.createRadialGradient(fx + along, fy + off, 0, fx + along, fy + off, r);
            g.addColorStop(0, `rgba(255,255,255,${alpha})`);
            g.addColorStop(0.55, `rgba(255,255,255,${alpha * 0.55})`);
            g.addColorStop(1, "rgba(255,255,255,0)");
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.ellipse(fx + along, fy + off, r * 1.25, r, 0, 0, TAU);
            ctx.fill();
        }
        ctx.restore();
    }
    const coreCell = cell(48, 24);
    {
        const g = ctx.createRadialGradient(coreCell.x + 14, coreCell.y + 12, 0, coreCell.x + 14, coreCell.y + 12, 22);
        g.addColorStop(0, "rgba(255,255,255,1)");
        g.addColorStop(0.35, "rgba(255,255,255,0.85)");
        g.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(coreCell.x + 18, coreCell.y + 12, 18, 9, 0, 0, TAU);
        ctx.fill();
    }
    // a prong: a thin spike, its base bright, tapering to nothing
    const prongCell = cell(64, 12);
    {
        const g = ctx.createLinearGradient(prongCell.x, 0, prongCell.x + 64, 0);
        g.addColorStop(0, "rgba(255,255,255,1)");
        g.addColorStop(0.45, "rgba(255,255,255,0.6)");
        g.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(prongCell.x, prongCell.y + 6);
        ctx.quadraticCurveTo(prongCell.x + 18, prongCell.y + 1, prongCell.x + 63, prongCell.y + 6);
        ctx.quadraticCurveTo(prongCell.x + 18, prongCell.y + 11, prongCell.x, prongCell.y + 6);
        ctx.fill();
    }

    // smoke: lumpy soft clouds, a few overlapping blobs each
    const cloud = (size: number, blobs: number, r0: number, r1: number, alpha: number): Texture => {
        const c = cell(size, size);
        const cx = c.x + size / 2, cy = c.y + size / 2;
        ctx.save();
        ctx.beginPath();
        ctx.rect(c.x, c.y, size, size);
        ctx.clip();
        for (let i = 0; i < blobs; i++) {
            const a = rand() * TAU, d = rand() * size * 0.16;
            const r = r0 + rand() * (r1 - r0);
            const bx = cx + Math.cos(a) * d, by = cy + Math.sin(a) * d;
            ctx.fillStyle = radial(bx, by, r, [[0, alpha], [0.5, alpha * 0.6], [1, 0]]);
            ctx.fillRect(c.x, c.y, size, size);
        }
        ctx.restore();
        return texture(c);
    };
    const smoke = [cloud(96, 7, 18, 30, 0.42), cloud(96, 8, 16, 28, 0.4), cloud(96, 6, 20, 32, 0.44)];
    const dust = [cloud(64, 6, 11, 19, 0.5), cloud(64, 7, 10, 18, 0.48)];

    // a spark: a bright head with a fading streak behind it (drawn pointing right)
    const sparkCell = cell(48, 10);
    {
        const g = ctx.createLinearGradient(sparkCell.x, 0, sparkCell.x + 44, 0);
        g.addColorStop(0, "rgba(255,255,255,0)");
        g.addColorStop(0.7, "rgba(255,255,255,0.75)");
        g.addColorStop(1, "rgba(255,255,255,1)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(sparkCell.x + 24, sparkCell.y + 5, 22, 2, 0, 0, TAU);
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(sparkCell.x + 44, sparkCell.y + 5, 2.6, 0, TAU);
        ctx.fill();
    }

    // stone chips: small irregular pieces with a darker edge (they're tinted the stone's colour)
    const polygon = (size: number, points: number, rMin: number, rMax: number, stretch = 1): Texture => {
        const c = cell(size, size);
        const cx = c.x + size / 2, cy = c.y + size / 2;
        ctx.beginPath();
        for (let i = 0; i < points; i++) {
            const a = (i / points) * TAU + (rand() - 0.5) * 0.5;
            const r = rMin + rand() * (rMax - rMin);
            const px = cx + Math.cos(a) * r * stretch, py = cy + Math.sin(a) * r;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fillStyle = "#fff";
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.35)";
        ctx.lineWidth = 1;
        ctx.stroke();
        return texture(c);
    };
    const chips = [polygon(16, 6, 3.5, 6), polygon(16, 5, 3, 6.5), polygon(16, 7, 4, 6)];

    // wood splinters: thin slivers, pointed at both ends
    const sliver = (w: number, h: number): Texture => {
        const c = cell(w, h);
        const cy = c.y + h / 2;
        ctx.beginPath();
        ctx.moveTo(c.x + 1, cy + (rand() - 0.5));
        ctx.lineTo(c.x + w * (0.3 + rand() * 0.2), c.y + 0.6);
        ctx.lineTo(c.x + w - 1, cy + (rand() - 0.5) * 1.5);
        ctx.lineTo(c.x + w * (0.4 + rand() * 0.2), c.y + h - 0.6);
        ctx.closePath();
        ctx.fillStyle = "#fff";
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.3)";
        ctx.lineWidth = 0.8;
        ctx.stroke();
        return texture(c);
    };
    const splinters = [sliver(24, 6), sliver(20, 5)];

    // glass shards: thin, see-through, with a bright edge where the light catches them
    const shard = (size: number): Texture => {
        const c = cell(size, size);
        const cx = c.x + size / 2, cy = c.y + size / 2;
        const points = 3 + Math.floor(rand() * 2);
        const corners: [number, number][] = [];
        for (let i = 0; i < points; i++) {
            const a = (i / points) * TAU + (rand() - 0.5) * 0.9;
            const r = size * (0.24 + rand() * 0.22);
            corners.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
        }
        ctx.beginPath();
        corners.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)));
        ctx.closePath();
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,1)";
        ctx.lineWidth = 1.2;
        ctx.stroke();
        return texture(c);
    };
    const shards = [shard(16), shard(14), shard(18)];

    const grainCell = cell(8, 8);
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(grainCell.x + 4, grainCell.y + 4, 2.6, 0, TAU);
    ctx.fill();

    const dotCell = cell(32, 32);
    ctx.fillStyle = radial(dotCell.x + 16, dotCell.y + 16, 15, [[0, 1], [0.6, 0.85], [1, 0]]);
    ctx.fillRect(dotCell.x, dotCell.y, 32, 32);

    const ringCell = cell(64, 64);
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(ringCell.x + 32, ringCell.y + 32, 28, 0, TAU);
    ctx.stroke();

    source.update();
    return {
        glow: texture(glowCell),
        flame: texture(flameCell),
        flameCore: texture(coreCell),
        prong: texture(prongCell),
        light: texture(lightCell),
        smoke,
        dust,
        spark: texture(sparkCell),
        chips,
        splinters,
        shards,
        grain: texture(grainCell),
        dot: texture(dotCell),
        ring: texture(ringCell)
    };
}

const shape = (): Shapes => (shapes ??= buildShapes());

// ---------------------------------------------------------------------------------------------- what's on screen

interface Roots {
    readonly layer: Layer
    /** what lies on the ground: casings, chips and splinters that came to rest */
    readonly ground: Container
    /** what flies: chips, splinters, casings in the air */
    readonly air: Container
    /**
     * what glows (added light: muzzle flashes, sparks), over what flies: kept apart from it, as the renderer starts a
     * new batch at every change of blend (0.84's first build mixed them: a draw call a shot in a firefight)
     */
    readonly glow: Container
    /**
     * smoke and dust, over the players and under the bushes (a puff over the bush a shooter hides in would show where
     * they are: the audit of 0.84's first build, which drew it over the bushes)
     */
    readonly smoke: Container
}

/** a glow that flashes and fades (a muzzle's, a spark burst's) */
interface Flash {
    readonly sprite: Sprite
    readonly born: number
    readonly life: number
    readonly alpha: number
    readonly s0: number
    readonly s1: number
    /** it stays on the muzzle of the gun that fired it while it lasts (a flash's light) */
    readonly follow?: Follow
    /** drawn at least once (see `_stepFlashes`) */
    shown?: boolean
}

/** a piece of a muzzle's flame kept on the gun while it burns: where on it, and at what angle to the aim */
interface Follow {
    readonly player: Player
    /** how far along the barrel's line from the player's middle, and to one side of it (game units, before sizeMod) */
    readonly along: number
    readonly side: number
    readonly angle: number
}

/**
 * a piece of a muzzle's flame (0.86): its tongue, its root, a prong. It grows out in its first frames and burns down:
 * `len` along it, `wid` across, in pixels at its peak
 */
interface Flame {
    readonly sprite: Sprite
    readonly born: number
    readonly life: number
    readonly alpha: number
    readonly len: number
    readonly wid: number
    /** the texture's own length and width (pixels), so `len` and `wid` are its drawn size */
    readonly texLen: number
    readonly texWid: number
    readonly follow: Follow
    /** world position and angle when the gun is gone (the shooter left the view) */
    x: number
    y: number
    rotation: number
    /** drawn at least once (see `_stepFlames`) */
    shown?: boolean
}

/** a gun that has been firing: it smokes from its barrel a while after it stops (0.86) */
interface Heat {
    readonly player: Player
    def: GunDefinition
    /** 0 to 1: how hot the barrel is */
    level: number
    lastShot: number
    lastStep: number
    nextWisp: number
    altFire: boolean | undefined
}

/** a puff of smoke or dust: blown out, slowed by the air, then drifting with the wind as it spreads and thins */
interface Puff {
    readonly sprite: Sprite
    x: number
    y: number
    vx: number
    vy: number
    readonly born: number
    readonly life: number
    readonly r0: number
    readonly r1: number
    readonly radius: number
    readonly alpha: number
    readonly spin: number
    readonly drift: number
    /** how fast it spreads: 2.2 a steady spread (dust, wisps); higher, most of it at once (a muzzle's gas bursting out) */
    readonly grow: number
    /** its own colour, and its floor: lit each frame by the light where it drifts (0.98.7) */
    readonly tint: number
    readonly layer: Layer
}

/** a spark: a bright streak arcing down, skipping once off the ground, cooling from white to orange as it dies */
interface Spark {
    readonly sprite: Sprite
    x: number
    y: number
    z: number
    vx: number
    vy: number
    vz: number
    readonly born: number
    readonly life: number
    bounced: boolean
    readonly hot: number
}

/** a piece that flies, bounces and lies a while: a chip, a splinter, a shard, a grain, a casing */
interface Bit {
    readonly sprite: Sprite
    readonly shadow: Sprite | undefined
    readonly roots: Roots
    x: number
    y: number
    z: number
    vx: number
    vy: number
    vz: number
    rot: number
    spin: number
    readonly scale: number
    readonly bounce: number
    readonly friction: number
    /** how long it lies once it stops (ms) */
    readonly lies: number
    readonly fade: number
    restAt: number
    /** it's a casing: its landing sound, how it sinks */
    readonly casing?: { readonly shell: boolean, readonly pitch: number, landed: boolean, readonly roof: Building | undefined }
    /** taken early (a cap was reached): fades fast from then */
    quickAt: number
    /** its own colour (it's shaded while it flies over the museum attic's dark; lying on the boards, the dark's over it) */
    readonly tint: number
}

/** a ripple spreading on water (a casing sinking) */
interface Ring {
    readonly sprite: Sprite
    readonly born: number
    readonly life: number
}

const between = (min: number, max: number): number => min + Math.random() * (max - min);
const lerpColor = (a: number, b: number, k: number): number => {
    const r = ((a >> 16) & 255) + (((b >> 16) & 255) - ((a >> 16) & 255)) * k;
    const g = ((a >> 8) & 255) + (((b >> 8) & 255) - ((a >> 8) & 255)) * k;
    const bl = (a & 255) + ((b & 255) - (a & 255)) * k;
    return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(bl);
};
const lighten = (color: number, k: number): number => lerpColor(color, 0xffffff, k);
/**
 * A colour a little darker (or, with a negative step, lighter), in one of three steps: a piece's colour varies only
 * so much. (The Canvas 2D renderer, drawing without a graphics card, makes and keeps a tinted copy of a shape for every
 * colour it's drawn in: the colours here are kept to a few, so it makes only a few.)
 */
const shade = (color: number, darker: boolean): number => {
    const step = Math.floor(Math.random() * 3);
    return darker ? lerpColor(color, 0x000000, step * 0.12) : lerpColor(color, 0xffffff, step * 0.1);
};
/** a spark cooling: white-hot, yellow, orange, red-orange (four steps, not a colour each frame: see shade) */
const SPARK_COLORS = [0xfff6dc, 0xffd98a, 0xffa64a, 0xff7d24] as const;

/** the class of the gun firing, remembered (worked out once per gun) */
const traitsCache = new Map<string, GunTraits>();
export function traitsOf(def: GunDefinition): GunTraits {
    let traits = traitsCache.get(def.idString);
    if (!traits) traitsCache.set(def.idString, traits = gunTraits(def));
    return traits;
}

/** what a muzzle shows, by the gun's class (0.86: the flame and its light, the gas jet, how a barrel heats) */
interface MuzzleLook {
    /** how big the flash is, overall (0: none) */
    readonly glow: number
    /** smoke puffs in the gas it blows out (a fraction is a chance of one more) */
    readonly puffs: number
    /** how big that smoke is, and its colour */
    readonly size: number
    readonly tint: number
    /** unburnt powder: sparks a shot (a fraction is a chance) */
    readonly sparks: number
    /** the flame's tongue: its length and width (pixels at a player's normal size) */
    readonly flame: number
    readonly width: number
    /** its prongs: how many (fewest, most) and how long */
    readonly prongs: readonly [number, number]
    readonly prong: number
    /** the light it casts: how far (pixels) and how bright (added light at its middle) */
    readonly light: number
    readonly lightAlpha: number
    /** how long the flame burns (ms: two to four frames) */
    readonly burn: number
    /** how much hotter a shot leaves the barrel (0 to 1: a hot barrel smokes once the shooting stops) */
    readonly heat: number
}
const MUZZLES: Readonly<Record<GunTraits["gunClass"], MuzzleLook>> = {
    pistol: { glow: 0.9, puffs: 1.5, size: 0.75, tint: 0xd8d5ce, sparks: 0.15, flame: 44, width: 19, prongs: [2, 3], prong: 29, light: 204, lightAlpha: 0.29, burn: 45, heat: 0.09 },
    smg: { glow: 1, puffs: 1.5, size: 0.8, tint: 0xd8d5ce, sparks: 0.1, flame: 52, width: 19, prongs: [2, 3], prong: 31, light: 216, lightAlpha: 0.26, burn: 42, heat: 0.06 },
    rifle: { glow: 1.3, puffs: 2, size: 1, tint: 0xd6d3cb, sparks: 0.15, flame: 81, width: 25, prongs: [3, 4], prong: 47, light: 288, lightAlpha: 0.34, burn: 50, heat: 0.08 },
    // (a muzzle brake: the flame forward is short, most of it goes out to the sides: _flameOf)
    sniper: { glow: 1.9, puffs: 3, size: 1.35, tint: 0xdedad2, sparks: 0.3, flame: 70, width: 28, prongs: [2, 3], prong: 39, light: 384, lightAlpha: 0.41, burn: 65, heat: 0.28 },
    shotgun: { glow: 2, puffs: 3.5, size: 1.4, tint: 0xbfbab0, sparks: 4, flame: 96, width: 42, prongs: [4, 6], prong: 44, light: 360, lightAlpha: 0.38, burn: 60, heat: 0.3 },
    launcher: { glow: 1.2, puffs: 2.5, size: 1.2, tint: 0xcfcac2, sparks: 0, flame: 65, width: 32, prongs: [0, 1], prong: 34, light: 276, lightAlpha: 0.31, burn: 70, heat: 0.2 },
    toy: { glow: 0, puffs: 0, size: 0, tint: 0, sparks: 0, flame: 0, width: 0, prongs: [0, 0], prong: 0, light: 0, lightAlpha: 0, burn: 0, heat: 0 }
};

/** the flame's colours: its tongue (orange-yellow), its root (white-hot yellow), its prongs, its light */
const FLAME_TINT = 0xff9d3a;
const FLAME_HEART_TINT = 0xffd27a;
const FLAME_SIDE_TINT = 0xff9535;
const CORE_TINT = 0xfff3c4;
const PRONG_TINT = 0xffb54d;
const LIGHT_TINT = 0xffb35c;
const LIGHT_HOT_TINT = 0xffd9a0;

/** what flies off each material, and its colour when the obstacle doesn't say */
const DEBRIS: Readonly<Record<ImpactKind, { readonly tint: number, readonly dust: number }>> = {
    metal: { tint: 0x8d8f91, dust: 0xbab4a8 },
    metal_heavy: { tint: 0x6f7173, dust: 0xb0aa9e },
    stone: { tint: 0x9a968e, dust: 0xcac3b5 },
    ceramic: { tint: 0xeceae4, dust: 0xe6e2da },
    wood: { tint: 0x8a6a45, dust: 0xd9c49b },
    tree: { tint: 0x5b4632, dust: 0xb89f78 },
    dirt: { tint: 0xc4b089, dust: 0xbfae8a },
    cardboard: { tint: 0xb08a5a, dust: 0xcdb48e },
    soft: { tint: 0x6e6658, dust: 0xa59e90 },
    glass: { tint: 0xd9f1ff, dust: 0xe8f4fa },
    foliage: { tint: 0x5d7a3a, dust: 0x8a9a6a }
};

class CombatFxClass {
    private readonly _layers = new Map<Layer, Roots>();
    private _flashes: Flash[] = [];
    private _flames: Flame[] = [];
    /** the guns that have been firing (by player id): their barrels smoke a while after */
    private readonly _heat = new Map<number, Heat>();
    private _puffs: Puff[] = [];
    private _sparks: Spark[] = [];
    private _bits: Bit[] = [];
    private _casings: Bit[] = [];
    private _rings: Ring[] = [];
    private readonly _pool: Sprite[] = [];
    private readonly _takes = new TakePicker();
    /** the casings' landing sounds: a magazine emptied is a few tinkles, not thirty at once */
    private readonly _casingSounds = new SoundThrottle(5, 120);
    private _now = 0;
    private _broken = false;

    get enabled(): boolean {
        if (this._broken) return false;
        try {
            return GameConsole.getBuiltInCVar("cv_combat_fx") !== false;
        } catch {
            return true;
        }
    }

    private get _tier(): Tier {
        if (softwareRendering()) return TIERS.low;
        const quality = WarLook.quality;
        return quality === "high" ? TIERS.high : quality === "low" ? TIERS.low : TIERS.medium;
    }

    // ----------------------------------------------------------------------------------------------- sprites

    private _rootsFor(layer: Layer): Roots {
        let roots = this._layers.get(layer);
        if (!roots) {
            const make = (zIndex: number): Container => {
                const container = new Container();
                container.zIndex = zIndex;
                return container;
            };
            roots = {
                layer,
                ground: make(ZIndexes.Decals + 0.6),
                air: make(ZIndexes.Players + 0.6),
                glow: make(ZIndexes.Players + 0.65),
                smoke: make(ZIndexes.Players + 0.7)
            };
            this._layers.set(layer, roots);
            this._attach(roots);
        }
        return roots;
    }

    /** each layer's effects go in the container the camera shows that layer in (which changes on the stairs) */
    private _attach(roots: Roots): void {
        const target = CameraManager.getContainer(roots.layer);
        if (roots.ground.parent === target) return;
        target.addChild(roots.ground, roots.air, roots.glow, roots.smoke);
    }

    private _sprite(texture: Texture, parent: Container, additive = false): Sprite {
        const sprite = this._pool.pop() ?? new Sprite();
        sprite.texture = texture;
        sprite.anchor.set(0.5);
        sprite.rotation = 0;
        sprite.scale.set(1);
        sprite.alpha = 1;
        sprite.tint = 0xffffff;
        sprite.blendMode = additive ? "add" : "normal";
        sprite.visible = true;
        parent.addChild(sprite);
        return sprite;
    }

    private _recycle(sprite: Sprite | undefined): void {
        if (!sprite) return;
        sprite.removeFromParent();
        if (this._pool.length < 400) this._pool.push(sprite);
        else sprite.destroy();
    }

    /**
     * under a roof the viewer sees (a building they're outside, on this floor): what happens there is hidden from them,
     * so nothing of it is drawn. Smoke drifts through walls and a flash's light is wider than the room: drawn, they'd
     * show a shooter inside (the audit of 0.84's first build)
     */
    private _underRoof(position: Vector, layer: Layer): boolean {
        const building = this._roofOver(position, layer);
        return building?.ceilingVisible === true;
    }

    /**
     * the building whose roof is over this point as the viewer's screen draws it: one drawn with the floor the point is
     * drawn with (0.85: a building whose floor merely counts its stairs as its own, an apartment block's upper floor, hid
     * a shooter on the flight below it from someone in the lobby who saw them; and the mansion's, the barn's and the
     * tavern's stairs, drawn with their ground floor, weren't under its roof)
     */
    private _roofOver(position: Vector, layer: Layer): Building | undefined {
        const drawnWith = getLayerContainer(layer, Game.layer);
        for (const building of Game.objects.getCategory(ObjectCategory.Building)) {
            if (building.dead && !building.definition.hasDamagedCeiling) continue;
            if (getLayerContainer(building.layer, Game.layer) !== drawnWith) continue;
            if (building.ceilingHitbox?.isPointInside(position)) return building;
        }
        return undefined;
    }

    // ---------------------------------------------------------------------------------------------- the muzzle

    /** where a gun's muzzle is drawn now (pixels), and the way it points: on the player as the screen shows them */
    private _muzzleAt(follow: Follow): { readonly x: number, readonly y: number, readonly rotation: number } {
        const { player } = follow;
        const rotation = player.container.rotation;
        const scale = player.sizeMod * PIXI_SCALE;
        const along = follow.along * scale, side = follow.side * scale;
        const cos = Math.cos(rotation), sin = Math.sin(rotation);
        return {
            x: player.container.position.x + along * cos - side * sin,
            y: player.container.position.y + along * sin + side * cos,
            rotation: rotation + follow.angle
        };
    }

    /**
     * a gun fired (its animation, objects/player.ts): the muzzle's flame and the light it casts, the gas it blows out
     * and the smoke left hanging, a sniper's dust, a hot barrel's smoke after. True when it drew the flame (the
     * player's own old flash picture is left out then: one flash, not two)
     */
    private _muzzle(player: Player, def: GunDefinition, altFire: boolean | undefined): boolean {
        const traits = traitsOf(def);
        const look = MUZZLES[traits.gunClass];
        if (look.puffs === 0 && look.glow === 0) return false;
        this._now = performance.now();
        const tier = this._tier;
        const offset = def.isDual ? (altFire ? -1 : 1) * def.leftRightOffset : (def.bulletOffset ?? 0);
        // (where the shooter stands: a barrel's tip poking past a wall under a roof's eaves hid one outside, 0.85)
        if (this._underRoof(player.position, player.layer)) return false;
        const { x, y, rotation: aim } = this._muzzleAt({ player, along: def.length, side: offset, angle: 0 });
        const muzzle = Vec(x / PIXI_SCALE, y / PIXI_SCALE);
        const roots = this._rootsFor(player.layer);
        const quiet = traits.suppressed;
        const flash = !quiet && !traits.flashless && look.glow > 0;

        // the flame and its light (none from a gun that shows no muzzle flash, whatever it is: the firework launcher's)
        if (flash) this._flameOf(roots, player, def, offset, look, tier, traits.gunClass);

        // the gas: a hot ball of it bursting out of the muzzle (it glows for an instant), then the jet it blows forward,
        // thinning into smoke that the air slows and the wind takes
        const s = player.sizeMod;
        if (flash) {
            this._flash(roots, x + Math.cos(aim) * look.flame * 0.35 * s, y + Math.sin(aim) * look.flame * 0.35 * s, 0xffc27a, 0.5,
                look.width * 0.04 * s, look.width * 0.075 * s, between(90, 130));
        }
        // (thick and quick: the smoke's soft picture holds about two fifths of its colour at its middle, so a puff drawn
        // at 0.4 showed at a sixth: 0.86's first build, whose gas the owner would hardly have seen. 0.87: bigger, and
        // from the muzzle on: 0.86's were a few small balls of it well out in front of the barrel, a smudge at the zoom
        // a match is played at)
        // (a sniper rifle's or a shotgun's blast leaves a big cloud hanging a while)
        const heavy = look.size > 1.2;
        const fan = traits.gunClass === "shotgun" ? 0.38 : 0.2;
        // (0.88, the owner: "maybe theres too mas smoke now": about a third fainter than 0.87's, a little smaller and
        // shorter-lived; and a gun firing on and on adds less with each shot, so a full-auto burst leaves one cloud, not a
        // wall in front of the shooter: the shots that follow one less than 160 ms before)
        const before = this._heat.get(player.id);
        const sustained = before !== undefined && before.player === player && this._now - before.lastShot < 160;
        // the ball of it at the muzzle: out at once and staying there, spreading wide (a suppressor lets only a little out)
        if (!quiet || Math.random() < 0.7 * tier.amount) {
            const a = aim + between(-0.08, 0.08);
            const speed = between(40, 80) * (quiet ? 0.5 : 1);
            this._puff(roots, x + Math.cos(a) * 3 * s, y + Math.sin(a) * 3 * s, Math.cos(a) * speed, Math.sin(a) * speed, look.tint,
                (quiet ? 0.32 : heavy ? 0.7 : 0.6) * (sustained ? 0.6 : 1),
                6 * look.size * s, between(34, 42) * look.size * (heavy ? 1.25 : 1) * (quiet ? 0.6 : 1) * s, between(500, 750) * (heavy ? 1.4 : 1), false, 6);
        }
        // the jet blown forward out of it, one cloud with it: the nearest of it slow, its front the furthest out and fastest,
        // close enough together to run into one another from the start (spaced out, they were a string of beads)
        let puffs = (look.puffs + 1) * tier.amount;
        puffs = Math.max(heavy ? 3 : 2, Math.floor(puffs) + (Math.random() < puffs % 1 ? 1 : 0));
        if (sustained) puffs = Math.max(1, Math.floor(puffs / 2));
        if (quiet) puffs = 0;
        for (let i = 0; i < puffs; i++) {
            const a = aim + between(-fan, fan);
            const step = puffs === 1 ? 0.5 : i / (puffs - 1);
            const out = (look.flame * (0.12 + 0.8 * step) + 6) * s;
            const speed = between(120, 200) * (0.5 + 0.7 * step) * (heavy ? 1.3 : 1);
            this._puff(roots, x + Math.cos(a) * out, y + Math.sin(a) * out, Math.cos(a) * speed, Math.sin(a) * speed, look.tint,
                heavy ? between(0.66, 0.76) : between(0.55, 0.66),
                12 * look.size * s, between(36, 46) * look.size * (heavy ? 1.3 : 1) * (0.85 + 0.35 * step) * s, between(900, 1400) * (heavy ? 1.4 : 1), false, 5);
        }
        // a heavy blast leaves a wide, thin cloud hanging over where it was fired, long after the jet has gone
        if (heavy && !quiet) {
            this._puff(roots, x + Math.cos(aim) * 16 * s, y + Math.sin(aim) * 16 * s, Math.cos(aim) * 30, Math.sin(aim) * 30, look.tint, 0.36,
                14 * s, between(60, 78) * s, between(2400, 3200), false, 3);
        }
        // a sniper rifle's muzzle brake throws its gas out to both sides (and a little back)
        if (traits.gunClass === "sniper" && !quiet) {
            for (const side of [-1, 1]) {
                for (let i = 0; i < 2; i++) {
                    const a = aim + side * between(1.35, 1.75) + (i === 1 ? side * 0.35 : 0);
                    const speed = between(140, 220) * (i === 1 ? 0.7 : 1);
                    this._puff(roots, x, y, Math.cos(a) * speed, Math.sin(a) * speed, look.tint, i === 1 ? 0.42 : 0.58, 6 * s, between(26, 36) * s, between(1000, 1500), false, 4.5);
                }
            }
        }
        // a sniper's blast kicks dust up off loose ground
        if ((traits.gunClass === "sniper" || traits.gunClass === "shotgun") && !quiet && this._dusty(muzzle, player.layer)) {
            for (let i = 0; i < Math.round(3 * tier.amount); i++) {
                const a = aim + between(-0.9, 0.9);
                const d = between(8, 22);
                const speed = between(40, 90);
                this._puff(roots, x + Math.cos(a) * d, y + Math.sin(a) * d, Math.cos(a) * speed, Math.sin(a) * speed, 0xa59479, 0.34, 6, between(18, 26), between(700, 1000), true);
            }
        }
        // unburnt powder: a few sparks out of a shotgun's or a revolver's barrel, now and then out of the others'
        let sparks = look.sparks >= 1 ? Math.round(look.sparks * between(0.75, 1.5)) : Math.random() < look.sparks ? 1 + Math.round(Math.random()) : 0;
        if (def.idString.includes("m1895")) sparks = Math.max(sparks, 2);
        if (quiet || traits.flashless) sparks = 0;
        for (let i = 0; i < Math.round(sparks * tier.amount); i++) {
            const a = aim + between(-0.28, 0.28);
            const speed = between(420, 900);
            this._spark(roots, x, y, 14, Math.cos(a) * speed, Math.sin(a) * speed, between(-40, 80), between(60, 140), 0.85);
        }

        // the barrel warms with every shot (and cools between): once the shooting stops, a hot one smokes (_stepHeat)
        // (kept on every quality: a long burst's gas thins on Low too, where the puffs' cap is smallest; only Medium and
        // High draw the wisps: `_stepHeat`. The audit of 0.87: Low never thinned a burst)
        if (look.heat > 0) {
            const heat = this._heat.get(player.id);
            if (heat && heat.player === player) {
                this._cool(heat, this._now);
                heat.level = Math.min(1, heat.level + look.heat);
                heat.def = def;
                heat.altFire = altFire;
                heat.lastShot = this._now;
            } else {
                this._heat.set(player.id, { player, def, level: look.heat, lastShot: this._now, lastStep: this._now, nextWisp: 0, altFire });
            }
        }
        return flash;
    }

    /**
     * the flame (0.86): a tongue of burning gas out of the muzzle with its white-hot root, a star of thin prongs round
     * it, different every shot, and the light it casts on the ground, the walls and the shooter for an instant. All of
     * it stays on the gun while it burns (two to four frames), whoever moves. A sniper rifle's muzzle brake sends most
     * of its flame out to the sides; a shotgun's is broad and short-pronged
     */
    private _flameOf(roots: Roots, player: Player, def: GunDefinition, side: number, look: MuzzleLook, tier: Tier, gunClass: GunTraits["gunClass"]): void {
        const k = between(0.82, 1.18);
        const burn = look.burn * between(0.85, 1.2);
        const s = player.sizeMod;
        const at = (angle: number, along = 0): Follow => ({ player, along: def.length + along, side, angle });
        // its light: a wide warm wash and a hotter middle (on the lowest look, the middle only)
        if (tier.light) {
            this._light(roots, at(0, 0.4), look.light * k * s, look.lightAlpha, burn * 1.7, LIGHT_TINT);
            this._light(roots, at(0, 0.6), look.light * 0.42 * k * s, look.lightAlpha * 1.3, burn * 1.15, LIGHT_HOT_TINT);
        } else {
            this._light(roots, at(0, 0.4), look.light * 0.35 * k * s, look.lightAlpha * 1.2, burn, LIGHT_TINT);
        }
        // the tongue
        const shape_ = shape();
        if (gunClass === "sniper") {
            this._flame(roots, shape_.flame, at(between(-0.05, 0.05)), look.flame * 0.75 * k * s, look.width * s, 0.85, burn, FLAME_TINT);
            for (const sign of [-1, 1]) {
                this._flame(roots, shape_.flame, at(sign * between(1.35, 1.75), -0.15), look.flame * between(0.85, 1.05) * k * s, look.width * 0.85 * s, 0.9, burn, FLAME_SIDE_TINT);
            }
        } else {
            const angle = between(-0.06, 0.06);
            this._flame(roots, shape_.flame, at(angle), look.flame * 1.12 * k * s, look.width * between(0.85, 1.15) * s, 1, burn, FLAME_TINT);
            // its hotter heart, yellow, inside the orange
            this._flame(roots, shape_.flame, at(angle), look.flame * 0.7 * k * s, look.width * 0.55 * s, 0.85, burn * 0.8, FLAME_HEART_TINT);
        }
        // its root, white-hot at the muzzle
        this._flame(roots, shape_.flameCore, at(0, -0.1), look.width * 1.25 * k * s, look.width * 0.62 * s, 0.92, burn * 0.7, CORE_TINT);
        // the prongs
        const [fewest, most] = look.prongs;
        const prongs = Math.round((fewest + Math.floor(Math.random() * (most - fewest + 1))) * (tier.amount < 0.5 ? 0.5 : 1));
        for (let i = 0; i < prongs; i++) {
            const angle = (Math.random() < 0.5 ? -1 : 1) * between(0.3, 1.2) * (gunClass === "shotgun" ? 0.65 : 1);
            this._flame(roots, shape_.prong, at(angle), look.prong * between(0.7, 1.3) * s, between(4, 6) * s, 0.95, burn * 0.8, PRONG_TINT);
        }
    }

    /** a piece of the flame (its tongue, root or a prong), kept on the gun while it burns */
    private _flame(roots: Roots, texture: Texture, follow: Follow, len: number, wid: number, alpha: number, life: number, tint: number): void {
        if (this._flames.length >= this._tier.flames) {
            // the oldest makes way
            const oldest = this._flames.shift();
            if (oldest) this._recycle(oldest.sprite);
        }
        const sprite = this._sprite(texture, roots.glow, true);
        // (from its base at the muzzle; the root a little behind it, so it covers the barrel's tip)
        sprite.anchor.set(texture === shape().flameCore ? 0.3 : 0, 0.5);
        sprite.tint = tint;
        const at = this._muzzleAt(follow);
        const flame: Flame = {
            sprite, born: this._now, life, alpha, len, wid, texLen: texture.frame.width, texWid: texture.frame.height, follow, x: at.x, y: at.y, rotation: at.rotation
        };
        this._placeFlame(flame, 0);
        this._flames.push(flame);
    }

    /** the light a flash casts, kept on the gun while it lasts */
    private _light(roots: Roots, follow: Follow, radius: number, alpha: number, life: number, tint: number): void {
        if (this._flashes.length > 60) return;
        const sprite = this._sprite(shape().light, roots.glow, true);
        sprite.tint = tint;
        const at = this._muzzleAt(follow);
        sprite.position.set(at.x, at.y);
        const s0 = (radius * 2) / shape().light.frame.width;
        sprite.scale.set(s0);
        sprite.alpha = alpha;
        this._flashes.push({ sprite, born: this._now, life, alpha, s0, s1: s0 * 1.12, follow });
    }

    /**
     * a flame `k` of the way through its life: it grows out to full in its first fifth, then burns down, shrinking a
     * little and fading (on the gun, where the screen draws it now; where it was last, once the shooter's gone)
     */
    private _placeFlame(flame: Flame, k: number): void {
        if (!flame.follow.player.destroyed) {
            const at = this._muzzleAt(flame.follow);
            flame.x = at.x;
            flame.y = at.y;
            flame.rotation = at.rotation;
        }
        const grow = k < 0.2 ? 0.6 + 2 * k : 1;
        const fade = k < 0.2 ? 1 : (1 - (k - 0.2) / 0.8) ** 1.5;
        flame.sprite.position.set(flame.x, flame.y);
        flame.sprite.rotation = flame.rotation;
        flame.sprite.scale.set((flame.len / flame.texLen) * grow * (1 - 0.15 * k), (flame.wid / flame.texWid) * (0.85 + 0.15 * grow));
        flame.sprite.alpha = flame.alpha * fade;
    }

    /** a barrel cooling: about half its heat gone every two seconds */
    private _cool(heat: Heat, now: number): void {
        heat.level *= Math.exp(-(now - heat.lastStep) / 2900);
        heat.lastStep = now;
    }

    /**
     * the barrels that have been firing: once one has stopped a moment, a hot one sends up thin wisps of smoke from its
     * muzzle (more the hotter it is) until it's cooled. A gun put away, or its player gone, smokes no more
     */
    private _stepHeat(now: number): void {
        for (const [id, heat] of this._heat) {
            this._cool(heat, now);
            const player = heat.player;
            if (player.destroyed || player.dead || player.activeItem.idString !== heat.def.idString) {
                this._heat.delete(id);
                continue;
            }
            // (a barrel barely warm smokes nothing, and is forgotten once it's been a while since its last shot)
            if (heat.level < 0.22) {
                if (now - heat.lastShot > 800) this._heat.delete(id);
                continue;
            }
            if (!this._tier.barrelSmoke || now - heat.lastShot < 160 || now < heat.nextWisp) continue;
            heat.nextWisp = now + between(70, 120) / Math.min(1, heat.level + 0.35);
            if (this._underRoof(player.position, player.layer)) continue;
            const def = heat.def;
            const side = def.isDual ? (heat.altFire ? -1 : 1) * def.leftRightOffset : (def.bulletOffset ?? 0);
            const { x, y, rotation } = this._muzzleAt({ player, along: def.length - 0.2, side, angle: 0 });
            // (it curls off the tip, a little forward and to one side, then the wind has it)
            const a = rotation + between(-0.9, 0.9);
            const speed = between(14, 36);
            this._puff(this._rootsFor(player.layer), x, y, Math.cos(a) * speed, Math.sin(a) * speed, 0xd9d6d0, between(0.24, 0.36) * Math.min(1, heat.level * 1.6),
                3, between(14, 22) * player.sizeMod, between(1100, 1800));
        }
    }

    // ------------------------------------------------------------------------------------------------ impacts

    /** a bullet struck an obstacle or a wall (objects/obstacle.ts, building.ts): what flies off it */
    private _impact(position: Vector, normal: number, kind: ImpactKind, layer: Layer, direction: Vector | undefined, damage: number, tint: number | undefined): void {
        // (a little way off the surface, on the side the bullet came from: many roofs reach past their outer walls, and
        // a wall shot from outside showed nothing, 0.85)
        if (this._underRoof(Vec.add(position, Vec.fromPolar(normal, 1.5)), layer)) return;
        this._now = performance.now();
        const tier = this._tier;
        const roots = this._rootsFor(layer);
        const x = position.x * PIXI_SCALE, y = position.y * PIXI_SCALE;
        const power = Math.min(1.6, Math.max(0.6, Math.sqrt(damage / 14)));
        const n = (count: number): number => {
            const v = count * power * tier.amount;
            return Math.floor(v) + (Math.random() < v % 1 ? 1 : 0);
        };
        const debris = DEBRIS[kind];
        const color = tint ?? debris.tint;
        // the way a bullet glances off: its direction mirrored in the surface
        const nx = Math.cos(normal), ny = Math.sin(normal);
        let rx = nx, ry = ny;
        if (direction) {
            const dot = direction.x * nx + direction.y * ny;
            rx = direction.x - 2 * dot * nx;
            ry = direction.y - 2 * dot * ny;
        }
        const glance = Math.atan2(ry, rx);
        const outward = (spread: number): number => (Math.random() < 0.5 ? glance : normal) + between(-spread, spread);

        switch (kind) {
            case "metal":
            case "metal_heavy": {
                this._flash(roots, x, y, 0xfff0c8, 0.85, between(0.45, 0.65) * power, 0.9 * power, 55);
                for (let i = 0, count = n(kind === "metal_heavy" ? 9 : 7); i < count; i++) {
                    const a = outward(0.75);
                    const speed = between(280, 820);
                    this._spark(roots, x, y, between(4, 26), Math.cos(a) * speed, Math.sin(a) * speed, between(40, 260), between(160, 420), 1);
                }
                if (Math.random() < 0.5) this._puff(roots, x, y, nx * 50, ny * 50, debris.dust, 0.22, 3, 14, 600, true);
                break;
            }
            case "stone":
            case "ceramic": {
                this._flash(roots, x, y, 0xffffff, 0.35, 0.35, 0.5, 35);
                for (let i = 0, count = n(5); i < count; i++) {
                    const a = outward(1.1);
                    const speed = between(120, 380);
                    this._bit(roots, shape().chips[i % 3], x, y, between(4, 20), Math.cos(a) * speed, Math.sin(a) * speed, between(150, 420),
                        shade(color, true), between(0.55, 0.95), 0.3, 0.5, between(1200, 2400), 600);
                }
                for (let i = 0, count = Math.max(1, n(2)); i < count; i++) {
                    const a = normal + between(-0.6, 0.6);
                    const speed = between(60, 140);
                    this._puff(roots, x, y, Math.cos(a) * speed, Math.sin(a) * speed, lighten(color, 0.45), between(0.5, 0.6), 4, between(20, 28), between(700, 1100), true);
                }
                break;
            }
            case "wood":
            case "tree": {
                for (let i = 0, count = n(kind === "tree" ? 3 : 4); i < count; i++) {
                    const a = outward(1.0);
                    const speed = between(150, 400);
                    this._bit(roots, shape().splinters[i % 2], x, y, between(4, 20), Math.cos(a) * speed, Math.sin(a) * speed, between(150, 380),
                        shade(color, false), between(0.6, 1), 0.25, 0.45, between(1000, 2000), 600, between(-22, 22));
                }
                this._puff(roots, x, y, nx * between(50, 100), ny * between(50, 100), lighten(debris.dust, 0.1), between(0.42, 0.52), 3, between(14, 20), between(500, 800), true);
                break;
            }
            case "dirt": {
                for (let i = 0, count = n(11); i < count; i++) {
                    const a = normal + between(-1.2, 1.2);
                    const speed = between(60, 260);
                    this._bit(roots, shape().grain, x, y, between(2, 10), Math.cos(a) * speed, Math.sin(a) * speed, between(200, 520),
                        shade(color, true), between(0.5, 1), 0.15, 0.3, between(300, 600), 400);
                }
                for (let i = 0, count = Math.max(1, n(2)); i < count; i++) {
                    const a = normal + between(-0.7, 0.7);
                    const speed = between(50, 120);
                    this._puff(roots, x, y, Math.cos(a) * speed, Math.sin(a) * speed, debris.dust, between(0.55, 0.65), 5, between(24, 32), between(800, 1200), true);
                }
                break;
            }
            case "cardboard": {
                // flakes of card and a puff of its dust
                for (let i = 0, count = n(3); i < count; i++) {
                    const a = outward(1.1);
                    const speed = between(100, 260);
                    this._bit(roots, shape().chips[i % 3], x, y, between(4, 14), Math.cos(a) * speed, Math.sin(a) * speed, between(120, 300),
                        shade(color, false), between(0.4, 0.65), 0.15, 0.3, between(800, 1500), 500);
                }
                this._puff(roots, x, y, nx * between(40, 80), ny * between(40, 80), debris.dust, between(0.35, 0.45), 3, between(12, 18), between(500, 800), true);
                break;
            }
            case "soft": {
                // a puff of fabric dust, nothing flying
                this._puff(roots, x, y, nx * between(40, 70), ny * between(40, 70), debris.dust, between(0.35, 0.45), 3, between(14, 20), between(600, 900), true);
                break;
            }
            case "glass": {
                this._flash(roots, x, y, 0xe8f6ff, 0.5, 0.4, 0.6, 45);
                for (let i = 0, count = n(6); i < count; i++) {
                    // glass flies on the way the bullet went as much as back off the pane
                    const a = direction && Math.random() < 0.4 ? Math.atan2(direction.y, direction.x) + between(-0.5, 0.5) : outward(1.1);
                    const speed = between(140, 420);
                    this._bit(roots, shape().shards[i % 3], x, y, between(6, 24), Math.cos(a) * speed, Math.sin(a) * speed, between(120, 360),
                        color, between(0.55, 1), 0.2, 0.4, between(1500, 3000), 800, between(-16, 16));
                }
                break;
            }
            case "foliage":
                break;
        }
    }

    /** a bullet struck a player's helmet or vest: sparks off the steel, and off a vest a puff of torn fabric */
    private _armorHit(position: Vector, normal: number, layer: Layer, piece: "helmet" | "vest"): void {
        if (this._underRoof(position, layer)) return;
        this._now = performance.now();
        const roots = this._rootsFor(layer);
        const tier = this._tier;
        const x = position.x * PIXI_SCALE, y = position.y * PIXI_SCALE;
        this._flash(roots, x, y, 0xfff4d8, 0.8, piece === "helmet" ? 0.55 : 0.4, 0.8, 50);
        const count = Math.round((piece === "helmet" ? 7 : 4) * tier.amount);
        for (let i = 0; i < count; i++) {
            const a = normal + between(-1.0, 1.0);
            const speed = between(260, 700);
            this._spark(roots, x, y, between(16, 34), Math.cos(a) * speed, Math.sin(a) * speed, between(40, 220), between(140, 340), 1);
        }
        if (piece === "vest") this._puff(roots, x, y, Math.cos(normal) * 60, Math.sin(normal) * 60, 0x77726a, 0.3, 3, 12, 500, true);
    }

    // ------------------------------------------------------------------------------------------------ casings

    /**
     * A casing thrown out of a gun (objects/player.ts: spawnCasingParticles, which works out where and how fast, as it
     * always has): it flies, spins, bounces and lies there. `velocity` in game units a second.
     */
    private _casing(frame: string, position: Vector, velocity: Vector, rotation: number, spin: number, layer: Layer, sizeMod: number, def: GunDefinition, fired: boolean): void {
        this._now = performance.now();
        const tier = this._tier;
        const roots = this._rootsFor(layer);
        // (the shapes first: made the first time, and a fault making them would leave the casing's sprite behind)
        const dot = shape().dot;
        const sprite = this._sprite(TrenchioSprite.getTexture(frame), roots.air);
        sprite.tint = AtticDark.shade(0xffffff, position, layer);
        const shadow = this._sprite(dot, roots.ground);
        shadow.tint = 0x000000;
        shadow.alpha = 0.3;
        const traits = traitsOf(def);
        const bit: Bit = {
            sprite,
            shadow,
            roots,
            x: position.x * PIXI_SCALE,
            y: position.y * PIXI_SCALE,
            z: 18 * sizeMod,
            vx: velocity.x * PIXI_SCALE * 0.8,
            vy: velocity.y * PIXI_SCALE * 0.8,
            vz: between(240, 360),
            rot: rotation,
            spin: spin * between(3, 5),
            // (a shotgun's shell is a big picture: drawn smaller, it lies on the ground at about a casing's size)
            scale: (frame.startsWith("casing_12ga") ? 0.42 : 0.5) * sizeMod,
            bounce: 0.35,
            friction: 0.5,
            lies: tier.casingLife,
            fade: 1500,
            restAt: 0,
            casing: { shell: frame.startsWith("casing_12ga"), pitch: traits.casingPitch, landed: false, roof: this._roofOver(position, layer) },
            quickAt: 0,
            tint: 0xffffff
        };
        this._place(bit);
        this._casings.push(bit);
        // a breath of gas out of the ejection port with it, now and then (0.86; not under a roof the viewer is outside of,
        // where the wind could carry it out past the roof's edge: the review of 0.86)
        if (fired && Math.random() < 0.45 * tier.amount && (traits.gunClass === "rifle" || traits.gunClass === "smg" || traits.gunClass === "sniper") && !this._underRoof(position, layer)) {
            const a = Math.atan2(velocity.y, velocity.x) + between(-0.4, 0.4);
            this._puff(roots, bit.x, bit.y, Math.cos(a) * between(30, 60), Math.sin(a) * between(30, 60), 0xd9d6d0, between(0.26, 0.36), 3, between(11, 15) * sizeMod, between(550, 850), false, 3.5);
        }
        // over the cap: the oldest still lying go quickly
        let over = -tier.casings;
        for (const old of this._casings) if (old.quickAt === 0) over++;
        for (const old of this._casings) {
            if (over <= 0) break;
            if (old.quickAt === 0) {
                old.quickAt = this._now;
                over--;
            }
        }
        this._trim(this._casings, tier.casings);
    }

    /**
     * a flood faster than the quick ones fade (30 rifles into sandbags make thousands of grains a second): the oldest go
     * at once, so a list never holds more than half again its cap (0.84's first build let it grow by all that came in
     * 400 ms, a thousand pieces and more: the audit)
     */
    private _trim(list: Bit[], cap: number): void {
        const most = Math.ceil(cap * 1.5);
        if (list.length <= most) return;
        for (const old of list.splice(0, list.length - most)) {
            this._recycle(old.sprite);
            this._recycle(old.shadow);
        }
    }

    // ---------------------------------------------------------------------------------------------- the pieces

    private _flash(roots: Roots, x: number, y: number, tint: number, alpha: number, s0: number, s1: number, life: number): void {
        if (this._flashes.length > 60) return;
        const sprite = this._sprite(shape().glow, roots.glow, true);
        sprite.tint = tint;
        sprite.position.set(x, y);
        sprite.scale.set(s0);
        sprite.alpha = alpha;
        this._flashes.push({ sprite, born: this._now, life, alpha, s0, s1 });
    }

    private _puff(roots: Roots, x: number, y: number, vx: number, vy: number, tint: number, alpha: number, r0: number, r1: number, life: number, dust = false, grow = 2.2): void {
        if (this._puffs.length >= this._tier.puffs) {
            // the oldest makes way
            const oldest = this._puffs.shift();
            if (oldest) this._recycle(oldest.sprite);
        }
        const textures = dust ? shape().dust : shape().smoke;
        const sprite = this._sprite(textures[Math.floor(Math.random() * textures.length)], roots.smoke);
        // (in the museum's attic, as dark as its dark is there: 1.04.2's audit, smoke and dust showed bright in a black room)
        sprite.tint = AtticDark.shade(tint, Vec(x / PIXI_SCALE, y / PIXI_SCALE), roots.layer);
        sprite.alpha = 0;
        sprite.rotation = Math.random() * TAU;
        sprite.position.set(x, y);
        this._puffs.push({
            sprite, x, y, vx, vy, born: this._now, life, r0, r1, radius: dust ? DUST_R : SMOKE_R, alpha,
            spin: between(-0.6, 0.6), drift: between(0.7, 1.3) * (dust ? 0.5 : 1), grow, tint: sprite.tint, layer: roots.layer
        });
    }

    private _spark(roots: Roots, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, hot: number): void {
        if (this._sparks.length >= this._tier.bits) return;
        const sprite = this._sprite(shape().spark, roots.glow, true);
        sprite.anchor.set(0.9, 0.5);
        sprite.position.set(x, y);
        this._sparks.push({ sprite, x, y, z, vx, vy, vz, born: this._now, life, bounced: false, hot });
    }

    private _bit(roots: Roots, texture: Texture, x: number, y: number, z: number, vx: number, vy: number, vz: number, tint: number, scale: number, bounce: number, friction: number, lies: number, fade: number, spin = between(-14, 14)): void {
        if (this._bits.length >= this._tier.bits) {
            const oldest = this._bits.find(bit => bit.quickAt === 0);
            if (oldest) oldest.quickAt = this._now;
            else return;
        }
        const sprite = this._sprite(texture, roots.air);
        sprite.tint = AtticDark.shade(tint, Vec(x / PIXI_SCALE, y / PIXI_SCALE), roots.layer);
        const shadow = scale > 0.7 ? this._sprite(shape().dot, roots.ground) : undefined;
        if (shadow) {
            shadow.tint = 0x000000;
            shadow.alpha = 0.22;
        }
        const bit: Bit = {
            // (0.98.7: a piece lies for the match, whatever it was asked to lie: the cap takes the oldest)
            sprite, shadow, roots, x, y, z, vx, vy, vz, rot: Math.random() * TAU, spin, scale, bounce, friction, lies: Math.max(lies, KEEP_MS), fade, restAt: 0, quickAt: 0,
            tint
        };
        this._place(bit);
        this._bits.push(bit);
        this._trim(this._bits, this._tier.bits);
    }

    private _place(bit: Bit): void {
        const lift = 1 + bit.z / (140 * M);
        bit.sprite.position.set(bit.x, bit.y);
        bit.sprite.scale.set(bit.scale * lift);
        bit.sprite.rotation = bit.rot;
        if (bit.shadow) {
            bit.shadow.position.set(bit.x + bit.z * 0.12, bit.y + bit.z * 0.2);
            bit.shadow.scale.set(Math.max(0.15, bit.scale * 0.32 * (1 - Math.min(0.6, bit.z / 120))));
            bit.shadow.visible = bit.z > 0.5;
        }
    }

    /** the weather's wind (the battlefield look's: war/warLook.ts), in pixels a second for the smoke */
    private _wind(): Vector {
        const wind = WarLook.weather.wind;
        return Vec(wind.x * 12, wind.y * 12);
    }

    private _dusty(position: Vector, layer: Layer): boolean {
        try {
            const floor = MapManager.terrain.getFloor(position, layer);
            return floor === "grass" || floor === "sand";
        } catch {
            return false;
        }
    }

    private _floor(x: number, y: number, layer: Layer): string | undefined {
        try {
            return MapManager.terrain.getFloor(Vec(x / PIXI_SCALE, y / PIXI_SCALE), layer);
        } catch {
            return undefined;
        }
    }

    // ------------------------------------------------------------------------------------------------- frames

    private _update(deltaMS: number): void {
        const now = this._now = performance.now();
        const dt = Math.min(deltaMS, 50) / 1000;
        for (const roots of this._layers.values()) this._attach(roots);
        this._stepFlashes(now);
        this._stepFlames(now);
        this._stepHeat(now);
        this._stepPuffs(now, dt);
        this._stepSparks(now, dt);
        this._bits = this._stepBits(this._bits, now, dt);
        this._casings = this._stepBits(this._casings, now, dt);
        this._stepRings(now);
    }

    private _stepFlashes(now: number): void {
        let w = 0;
        for (const f of this._flashes) {
            // (one shorter-lived than a slow frame was recycled before it was ever drawn: at 20 to 30 frames a second,
            // up to two in five shots lost their light or their flame's root. Each is drawn once at least, half burnt
            // out, however late the frame: the audit of 0.87)
            const age = (now - f.born) / f.life;
            if (age >= 1 && f.shown) {
                this._recycle(f.sprite);
                continue;
            }
            const k = age >= 1 ? 0.6 : age;
            f.shown = true;
            f.sprite.alpha = f.alpha * (1 - k) ** 2;
            f.sprite.scale.set(f.s0 + (f.s1 - f.s0) * k);
            if (f.follow && !f.follow.player.destroyed) {
                const at = this._muzzleAt(f.follow);
                f.sprite.position.set(at.x, at.y);
            }
            this._flashes[w++] = f;
        }
        this._flashes.length = w;
    }

    private _stepFlames(now: number): void {
        let w = 0;
        for (const f of this._flames) {
            // (drawn once at least, however late the frame: `_stepFlashes`)
            const age = (now - f.born) / f.life;
            if (age >= 1 && f.shown) {
                this._recycle(f.sprite);
                continue;
            }
            f.shown = true;
            this._placeFlame(f, age >= 1 ? 0.6 : age);
            this._flames[w++] = f;
        }
        this._flames.length = w;
    }

    private _stepPuffs(now: number, dt: number): void {
        const wind = this._wind();
        const drag = Math.exp(-dt / 0.22);
        let w = 0;
        for (const p of this._puffs) {
            const k = (now - p.born) / p.life;
            if (k >= 1) {
                this._recycle(p.sprite);
                continue;
            }
            p.vx *= drag;
            p.vy *= drag;
            // blown out, slowed by the air, then carried by the wind (more of it as it slows)
            p.x += (p.vx + wind.x * p.drift * Math.min(1, k * 3)) * dt;
            p.y += (p.vy + wind.y * p.drift * Math.min(1, k * 3)) * dt;
            const r = p.r0 + (p.r1 - p.r0) * (1 - (1 - k) ** p.grow);
            p.sprite.position.set(p.x, p.y);
            p.sprite.scale.set(r / p.radius);
            p.sprite.rotation += p.spin * dt;
            // (it holds most of its thickness for the first half of its life, then thins away)
            p.sprite.alpha = p.alpha * Math.min(1, k / 0.05) * (1 - k ** 1.6);
            // (0.98.7: lit by the light where it drifts: the sun's colour, a fire's warmth beside it, the night's dark)
            p.sprite.tint = multiplyTint(p.tint, Lighting.litTint(p.x / PIXI_SCALE, p.y / PIXI_SCALE, p.layer));
            this._puffs[w++] = p;
        }
        this._puffs.length = w;
    }

    private _stepSparks(now: number, dt: number): void {
        const drag = Math.exp(-dt * 2.2);
        let w = 0;
        for (const s of this._sparks) {
            const k = (now - s.born) / s.life;
            if (k >= 1) {
                this._recycle(s.sprite);
                continue;
            }
            s.vx *= drag;
            s.vy *= drag;
            s.vz -= GRAVITY * dt;
            s.x += s.vx * dt;
            s.y += s.vy * dt;
            s.z += s.vz * dt;
            if (s.z <= 0) {
                s.z = 0;
                if (!s.bounced) {
                    s.bounced = true;
                    s.vz = -s.vz * 0.35;
                    s.vx *= 0.55;
                    s.vy *= 0.55;
                } else {
                    s.vz = 0;
                }
            }
            const speed = Math.hypot(s.vx, s.vy);
            s.sprite.position.set(s.x, s.y);
            s.sprite.rotation = Math.atan2(s.vy, s.vx);
            s.sprite.scale.set(Math.min(2.2, Math.max(0.35, speed / 420)) * (1 + s.z / 200), 0.65 * (1 + s.z / 200));
            s.sprite.tint = SPARK_COLORS[Math.min(3, Math.floor(k * 5.6 / s.hot))];
            s.sprite.alpha = (1 - k) ** 0.8;
            this._sparks[w++] = s;
        }
        this._sparks.length = w;
    }

    private _stepBits(list: Bit[], now: number, dt: number): Bit[] {
        let w = 0;
        for (const b of list) {
            // lying: fading once its time is up (or quickly, when a cap took it)
            if (b.restAt > 0 || b.quickAt > 0) {
                const fadeFrom = b.quickAt > 0 ? b.quickAt : b.restAt + b.lies;
                const fadeFor = b.quickAt > 0 ? 400 : b.fade;
                const k = (now - fadeFrom) / fadeFor;
                if (k >= 1) {
                    this._recycle(b.sprite);
                    this._recycle(b.shadow);
                    continue;
                }
                if (k > 0) {
                    b.sprite.alpha = 1 - k;
                    if (b.shadow) b.shadow.alpha = 0.25 * (1 - k);
                }
                if (b.restAt > 0) {
                    list[w++] = b;
                    continue;
                }
            }
            b.vz -= GRAVITY * dt;
            b.x += b.vx * dt;
            b.y += b.vy * dt;
            b.z += b.vz * dt;
            b.rot += b.spin * dt;
            if (b.z <= 0) {
                b.z = 0;
                if (b.casing && !b.casing.landed) {
                    b.casing.landed = true;
                    if (this._landCasing(b)) continue;
                }
                if (Math.abs(b.vz) < 90) {
                    // it comes to rest where it is, and lies on the ground (under the players)
                    b.vx = b.vy = b.vz = 0;
                    b.restAt = now;
                    b.roots.ground.addChild(b.sprite);
                    b.sprite.tint = b.tint;
                    if (b.shadow) b.shadow.visible = false;
                } else {
                    b.vz = -b.vz * b.bounce;
                    b.vx *= b.friction;
                    b.vy *= b.friction;
                    b.spin *= 0.5;
                }
            }
            this._place(b);
            list[w++] = b;
        }
        list.length = w;
        return list;
    }

    /** a casing's first touch of the ground: its sound (by the floor), and in water it sinks (true: it's gone) */
    private _landCasing(b: Bit): boolean {
        const casing = b.casing;
        if (!casing) return false;
        // landing under another roof than it was thrown out under (or out from under it): it flew through a wall, so it's
        // gone, without a sound (0.85: a shooter's casings lay ringing outside the building they were in)
        if (this._roofOver(Vec(b.x / PIXI_SCALE, b.y / PIXI_SCALE), b.roots.layer) !== casing.roof) {
            this._recycle(b.sprite);
            this._recycle(b.shadow);
            return true;
        }
        const floor = this._floor(b.x, b.y, b.roots.layer);
        const sound = floor === undefined ? undefined : casingSound(floor, casing.shell);
        const position = Vec(b.x / PIXI_SCALE, b.y / PIXI_SCALE);
        // (a browser holding an older sound sheet has none of them: nothing is played, rather than a warning a landing)
        if (sound && CombatSounds.ready && Vec.len(Vec.sub(SoundManager.position, position)) < 34 && this._casingSounds.allow(sound.sound, this._now, 0)) {
            SoundManager.play(this._takes.pick(sound.sound), {
                position,
                falloff: 0.6,
                maxRange: 36,
                layer: b.roots.layer,
                speed: casing.pitch,
                volume: sound.volume,
                variation: { pitch: 0.07, volume: 0.15 },
                air: true
            });
        }
        if (floor === "water") {
            const sprite = this._sprite(shape().ring, b.roots.ground);
            sprite.position.set(b.x, b.y);
            sprite.scale.set(0.1);
            sprite.alpha = 0.5;
            this._rings.push({ sprite, born: this._now, life: 700 });
            this._recycle(b.sprite);
            this._recycle(b.shadow);
            return true;
        }
        return false;
    }

    private _stepRings(now: number): void {
        let w = 0;
        for (const r of this._rings) {
            const k = (now - r.born) / r.life;
            if (k >= 1) {
                this._recycle(r.sprite);
                continue;
            }
            r.sprite.scale.set(0.1 + 0.45 * Math.sqrt(k));
            r.sprite.alpha = 0.5 * (1 - k);
            this._rings[w++] = r;
        }
        this._rings.length = w;
    }

    private _clearAll(): void {
        for (const f of this._flashes) this._recycle(f.sprite);
        for (const f of this._flames) this._recycle(f.sprite);
        this._flames = [];
        this._heat.clear();
        for (const p of this._puffs) this._recycle(p.sprite);
        for (const s of this._sparks) this._recycle(s.sprite);
        for (const b of [...this._bits, ...this._casings]) {
            this._recycle(b.sprite);
            this._recycle(b.shadow);
        }
        for (const r of this._rings) this._recycle(r.sprite);
        this._flashes = [];
        this._puffs = [];
        this._sparks = [];
        this._bits = [];
        this._casings = [];
        this._rings = [];
    }

    private _reset(): void {
        this._clearAll();
        for (const sprite of this._pool) sprite.destroy();
        this._pool.length = 0;
        for (const roots of this._layers.values()) {
            for (const container of [roots.ground, roots.air, roots.glow, roots.smoke]) container.destroy({ children: true });
        }
        this._layers.clear();
    }

    // ------------------------------------------------------------------------------------------- the game's calls

    private _safely(task: () => void): void {
        if (this._broken) return;
        try {
            task();
        } catch (error) {
            this._broken = true;
            console.error("The combat effects stopped after an error (the game carries on without them):", error);
            try {
                this._clearAll();
            } catch {
                // what's left stays where it is
            }
        }
    }

    /** a gun fired: true when its flame was drawn here (the player's old flash picture is left out then) */
    muzzle(player: Player, def: GunDefinition, altFire: boolean | undefined): boolean {
        let flame = false;
        if (this.enabled) this._safely(() => { flame = this._muzzle(player, def, altFire); });
        return flame;
    }

    /** a bullet struck something (`normal`: the surface's outward angle; `tint`: the obstacle's own colour, if known) */
    impact(position: Vector, normal: number, kind: ImpactKind, layer: Layer, direction: Vector | undefined, damage: number, tint?: number): void {
        if (this.enabled) this._safely(() => this._impact(position, normal, kind, layer, direction, damage, tint));
    }

    /** a bullet struck a player's armour */
    armorHit(position: Vector, normal: number, layer: Layer, piece: "helmet" | "vest"): void {
        if (this.enabled) this._safely(() => this._armorHit(position, normal, layer, piece));
    }

    /**
     * a casing thrown out of a gun (`fired`: by a shot or the bolt cycled after one, not a reload's emptied casings or
     * clip); false when the effects are off (the caller draws its plain one)
     */
    casing(frame: string, position: Vector, velocity: Vector, rotation: number, spin: number, layer: Layer, sizeMod: number, def: GunDefinition, fired = true): boolean {
        if (!this.enabled) return false;
        let made = false;
        this._safely(() => {
            this._casing(frame, position, velocity, rotation, spin, layer, sizeMod, def, fired);
            made = true;
        });
        return made;
    }

    /**
     * a bright head for a rifle's or sniper's tracer (the bullet places it and destroys it), or undefined: not this
     * gun, or the effects are off
     */
    tracerHead(def: GunDefinition | undefined, tint: number): Sprite | undefined {
        if (!def || !this.enabled) return undefined;
        const traits = traitsOf(def);
        if ((traits.gunClass !== "rifle" && traits.gunClass !== "sniper") || traits.suppressed) return undefined;
        try {
            const sprite = new Sprite(shape().glow);
            sprite.anchor.set(0.5);
            sprite.blendMode = "add";
            sprite.tint = lighten(tint, 0.55);
            sprite.alpha = 0.9;
            sprite.scale.set(traits.gunClass === "sniper" ? 0.42 : 0.3);
            return sprite;
        } catch {
            return undefined;
        }
    }

    /** every frame */
    update(deltaMS: number): void { this._safely(() => this._update(deltaMS)); }
    /** the setting went off: everything drawn goes at once */
    clear(): void { this._safely(() => this._clearAll()); }
    /** a new world (a new match, the death replay, a switch of whose view is watched) */
    reset(): void { this._safely(() => this._reset()); }
}

/** the colour of the bits off an obstacle whose particles are tinted (common/src/definitions/obstacles.ts) */
export function particleTint(frames: readonly string[] | undefined): number | undefined {
    const frame = frames?.[0];
    return frame === undefined ? undefined : TintedParticles[frame]?.tint;
}

export const CombatFx = new CombatFxClass();

/** (the lab's look at the shapes: how many tinted copies the Canvas 2D renderer keeps of each) */
export const __shapesForTests = (): Shapes | undefined => shapes;
